import { useNavigate } from 'react-router-dom';
import { AlertTriangle, TrendingUp } from 'lucide-react';
import { useModuleLimits } from '@/hooks/useModuleLimits';
import { type B2BModule } from '@/config/plans';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface ModuleLimitBannerProps {
  module: B2BModule;
  entityName?: string; // "véhicules", "biens", etc.
}

const MODULE_LABELS: Record<string, string> = {
  fleet: 'véhicules',
  vehicles: 'véhicules',
  properties: 'biens',
  subcontractors: 'prestataires',
  tenders: 'appels d\'offres',
};

/**
 * Banner component showing usage limits for B2B modules
 */
export function ModuleLimitBanner({ module, entityName }: ModuleLimitBannerProps) {
  const navigate = useNavigate();
  const { limits, isNearLimit, isAtLimit, isLoading } = useModuleLimits();

  // Map module to limits key
  const limitsKey = module === 'fleet' ? 'vehicles' : module;
  const moduleLimit = limits[limitsKey as keyof typeof limits];
  const label = entityName || MODULE_LABELS[module] || MODULE_LABELS[limitsKey];

  // Don't show for unlimited plans
  if (moduleLimit.total === Infinity) {
    return null;
  }

  // Don't show if loading or not near limit
  if (isLoading || (!isNearLimit(module) && !isAtLimit(module))) {
    return null;
  }

  const atLimit = isAtLimit(module);

  return (
    <Alert variant={atLimit ? "destructive" : "default"} className="mb-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {atLimit ? (
            <AlertTriangle className="h-4 w-4 shrink-0" />
          ) : (
            <TrendingUp className="h-4 w-4 shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <AlertDescription className="flex items-center gap-3">
              <span className="font-medium">
                {moduleLimit.current}/{moduleLimit.total} {label}
              </span>
              <Progress 
                value={moduleLimit.percentage} 
                className="h-2 w-24 shrink-0" 
              />
              {atLimit && (
                <span className="text-sm">Limite atteinte</span>
              )}
            </AlertDescription>
          </div>
        </div>
        <Button 
          variant={atLimit ? "default" : "outline"} 
          size="sm"
          onClick={() => navigate('/pricing')}
        >
          {atLimit ? 'Augmenter ma limite' : 'Voir les options'}
        </Button>
      </div>
    </Alert>
  );
}
